import type { QueryClient } from "@tanstack/react-query";

import { belongsToCurrentImpersonation } from "../auth/impersonateStore";
import { QUERY_CACHE_STORAGE_KEY } from "./client";

// ---------------------------------------------------------------------------
// cacheIdentity — im Cache liegen die Daten genau EINER Sicht
// ---------------------------------------------------------------------------
// Der Engpass, durch den JEDER Wechsel der angezeigten Person laeuft:
// Anmeldung, Abmeldung, Vertretung starten, beenden, wechseln. Wechselt die
// Sicht, faellt der alte Stand weg — Speicher, Spiegel und (ueber die
// Identitaets-Epoche im AuthContext) die montierten Beobachter.
//
// Die Geschichte in vier Bausteinen, weil jeder einen anderen Traeger des
// alten Standes gefunden hat:
//
//   STABIL-4  — die Abfrage-Schluessel tragen keine Kennung
//               (`["notifications","list"]` gilt fuer jeden). Also raeumt
//               diese Stelle beim Wechsel Speicher UND localStorage-Spiegel.
//   STABIL-12 — `queryClient.clear()` benachrichtigt montierte Beobachter
//               NICHT. Ein `useQuery`, das auf dem Bildschirm steht, haelt
//               sein letztes Ergebnis in der eigenen Instanz und rendert
//               unveraendert weiter. Deshalb haengt am AuthProvider ein
//               `key`, der beim Wechsel den ganzen Baum verwirft.
//   STABIL-13 — der Vergleichswert lag im falschen Geltungsbereich; er lebt
//               seitdem IM MODUL, und die Kennung reist IM Spiegel.
//   VTR-7     — siehe unten: die Kennung war eine Person, der Zustand ist
//               aber ein PAAR.
//
// ---------------------------------------------------------------------------
// VTR-7 — «Marvin ist angemeldet» und «Daniel handelt als Marvin» sahen gleich aus
// ---------------------------------------------------------------------------
// Befund Daniel 18.08.2026 (v6.581.0, Produktion): Er handelte als Marvin und
// sah auf dessen Startseite vier Karten, die Marvin nicht gehoeren — zwei
// «IT-Alarm pruefen», zwei «Problem gemeldet». Beide Typen sind
// superadmin-eigen, Marvin ist `pl`.
//
// Am Server lag es nicht; das ist gemessen, nicht vermutet. Derselbe Endpunkt,
// zweimal gegen Produktion gerufen:
//
//   Bearer Daniel, ohne Vertretung         → 4 Karten, genau diese vier
//   Bearer Daniel, x-act-as-user-id Marvin → 18 Karten, keine davon Daniels
//
// Der Browser zeigte also Wort fuer Wort Daniels EIGENE Antwort unter Marvins
// Namen.
//
// Durchgelassen hat sie die Kennung selbst. Sie war die des ANGEZEIGTEN — eine
// einzelne Person. Damit ergeben zwei verschiedene Zustaende dieselbe Kennung:
//
//   Marvin meldet sich an      → "Marvin"
//   Daniel handelt als Marvin  → "Marvin"
//
// Ein Spiegel aus dem einen passt auf den anderen. Und weil die Vertretung im
// `sessionStorage` genau EINES Tabs lebt, der Spiegel aber allen Tabs gehoert,
// treffen die beiden Zustaende im selben Browser regelmaessig aufeinander:
// eine Registerkarte mit Daniels eigener Sicht, daneben eine, in der er Marvin
// vertritt. Wer zuletzt schreibt, gewinnt den Platz — und der andere Tab
// uebernimmt ihn beim naechsten Aufschlagen, weil nichts daran ihn als fremd
// ausweist.
//
// Die Kennung ist deshalb ab hier das PAAR: wer angemeldet ist UND wen dieser
// Tab vertritt. Das Paar bestimmt vollstaendig, was der Server antworten wird
// — es sind genau die beiden Angaben, die der Client auf die Leitung legt
// (`Authorization` und `x-act-as-user-id`). Und, wichtiger als alles andere:
// die Vertretungs-Haelfte ist SYNCHRON bekannt, aus dem `sessionStorage`,
// bevor `/api/me` ueberhaupt gefragt wurde.
//
// Genau daran haengt der Fix. Vorher konnte ein fremder Spiegel erst eine
// Rundreise spaeter widerlegt werden, und solange stand er auf dem Schirm.
// Jetzt wird er beim LESEN geprueft — vor dem ersten Bild.

/**
 * Wessen Sicht liegt im Cache?
 *
 * Beide Haelften zusammen bestimmen die Antwort des Servers. `anmelderId` ist
 * die Person, deren Anmeldung gilt (bei einer Vertretung: der Vertreter);
 * `vertretenerId` ist, wen dieser Tab vertritt — `null`, wenn niemand.
 */
export interface CacheOwner {
  readonly anmelderId: string;
  readonly vertretenerId: string | null;
}

// Das Trennzeichen macht den Schluessel lesbar UND unterscheidbar von den
// Stempeln aus der Zeit vor VTR-7: eine nackte UUID ohne `>` ist ein alter
// Stand und wird verworfen — dieselbe einmalige, fail-safe Raeumung wie bei
// STABIL-13.
const SEP = ">";

/** Der Schluessel zu einer Sicht. `null` heisst abgemeldet. */
export function identityKey(owner: CacheOwner | null): string | null {
  if (owner === null) return null;
  return `${owner.anmelderId}${SEP}${owner.vertretenerId ?? ""}`;
}

/**
 * Passt ein gespiegelter Stempel zu der Vertretung, die DIESER Tab fuehrt?
 *
 * Das ist die Haelfte, die ohne Nachfragen zu haben ist — und genau die, an
 * der sich «Marvin ist angemeldet» von «Daniel handelt als Marvin»
 * unterscheiden laesst. Ein Stempel ohne Trennzeichen stammt aus der Zeit vor
 * VTR-7 und kann die Frage nicht beantworten; er gilt als nicht passend.
 */
function stempelPasstZurVertretung(stempel: unknown): boolean {
  if (typeof stempel !== "string") return false;
  const i = stempel.indexOf(SEP);
  if (i === -1) return false;
  const vertretener = stempel.slice(i + 1);
  return belongsToCurrentImpersonation(vertretener === "" ? null : vertretener);
}

/**
 * Wem gehoert der Cache DIESES Tabs?
 *
 * `undefined` heisst «noch nicht bekannt» — beim Start, solange weder ein
 * Spiegel angenommen noch eine Sicht uebernommen wurde. Der Wert ist bewusst
 * von `null` («abgemeldet») verschieden: bei «unbekannt» wird geraeumt, denn
 * ein Cache unbekannter Herkunft ist genau das, wovor diese Datei schuetzt.
 */
let cacheIdentity: string | null | undefined = undefined;

/**
 * Prueft den Stempel eines wiederhergestellten Spiegels und meldet, ob sein
 * Inhalt uebernommen werden darf.
 *
 * Der Aufrufer (QueryProvider) wirft den Inhalt weg, wenn hier `false` steht —
 * er darf ihn NICHT hydrieren. Vor VTR-7 hat diese Stelle den Stempel blind
 * uebernommen; damit galt der Cache als geklaert, obwohl niemand ihn geprueft
 * hatte.
 */
export function acceptRestoredCacheIdentity(stempel: unknown): boolean {
  if (!stempelPasstZurVertretung(stempel)) {
    // «Unbekannt», nicht «fremd»: der naechste Abgleich raeumt dadurch in
    // jedem Fall — auch dann, wenn die Sicht danach zufaellig dieselbe waere.
    cacheIdentity = undefined;
    return false;
  }
  cacheIdentity = stempel as string;
  return true;
}

/** Nur fuer den QueryProvider: der Stempel, der in den Spiegel geht. */
export function currentCacheIdentity(): string | null | undefined {
  return cacheIdentity;
}

/** Nur fuer Tests: vergisst, wem der Cache gehoert. */
export function _resetCacheIdentityForTests(): void {
  cacheIdentity = undefined;
}

/**
 * Uebergibt den Cache an `owner` (`null` heisst abgemeldet).
 *
 * Ist es dieselbe Sicht wie zuletzt IN DIESEM TAB, bleibt alles stehen und die
 * Funktion meldet `false`. Sonst wird geraeumt und `true` gemeldet — der
 * Aufrufer zaehlt dann die Identitaets-Epoche hoch und verwirft damit auch die
 * montierten Beobachter.
 */
export function adoptCacheIdentity(
  queryClient: QueryClient,
  owner: CacheOwner | null,
): boolean {
  if (typeof window === "undefined") return false;

  const next = identityKey(owner);
  if (cacheIdentity !== undefined && cacheIdentity === next) return false;

  // Die Reihenfolge traegt die Sicherheit: erst den Spiegel wegwerfen, dann
  // den Speicher, zuletzt die neue Kennung setzen. Bricht es dazwischen ab
  // (Tab zu, Absturz), startet der naechste Lauf mit einem LEEREN Cache —
  // nie mit dem einer fremden Sicht.
  window.localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_IDENTITY_KEY);
  queryClient.clear();
  cacheIdentity = next;
  return true;
}

// Der tab-uebergreifende Marker aus STABIL-4. Er wird nicht mehr gelesen und
// nicht mehr geschrieben — aber er liegt noch in jedem Browser, der das OS
// schon benutzt hat. Beim ersten Raeumen raeumen wir ihn mit weg, damit
// niemand ihn spaeter fuer eine lebende Quelle haelt.
const LEGACY_IDENTITY_KEY = "bergler.query-cache.identity";
